import React, { useEffect, useState, useRef } from "react";
import Layout from "../../Layout";
import { Calendar, momentLocalizer, Views } from "react-big-calendar";
import moment from "moment-timezone";
import "react-big-calendar/lib/css/react-big-calendar.css";
import { BiChevronLeft, BiChevronRight, BiUserX } from "react-icons/bi";
import { HiOutlineViewGrid } from "react-icons/hi";
import { HiOutlineCalendarDays } from "react-icons/hi2";
import { FaRegCalendarAlt } from "react-icons/fa";
import * as Tooltip from "@radix-ui/react-tooltip";
import NowIndicatorLabel from "../Calendar/NowIndicator";
import { useSelector } from "react-redux";
import orderApi from "../../api/order/OrderApi";
import Loading from "../../Util/Loading";
import ProcessingBar2 from "../../components/ProcessingBar/ProcessingBar2";
import CustomEventOrdering from "../../components/OrderingComponents/CustomEventOrdering";
import { Tooltip as ReactTooltip } from "react-tooltip";
import CustomToolbarOrdering from "../../components/OrderingComponents/CustomToolbarOrdering";
import { socketId } from "../../configs/axiosConfig";
import OrderNotificationModal from "../../components/OrderNotification/OrderNotificationModal";

const localizer = momentLocalizer(moment);

function OrdersCalendar() {
  const operator = useSelector((state) => state.user?.user);
  const [orders, setOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  //socket section -----------------------

  const seller = operator;

  const [modalOpen, setModalOpen] = useState(false);
  const [orderData, setOrderData] = useState({});

  const [onlineUsers, setOnlineUsers] = useState([]);

  useEffect(() => {
    if (!seller?._id) return;

    socketId.emit("addUser", seller._id);

    const handleGetUsers = (data) => {
      setOnlineUsers(data);
      console.log("Updated online users:", data);
    };

    socketId.off("getUsers").on("getUsers", handleGetUsers); // safely replace any existing

    return () => {
      socketId.off("getUsers", handleGetUsers);
    };
  }, [socketId, seller?._id]);

  useEffect(() => {
    socketId.on("getOrderNotification", (data) => {
      setOrderData(data);
      setModalOpen(true);
      console.log("📦 New Order Received:", data);
      // Optional: Show toast or trigger refresh
      // toast.info(`New Order: ${data.orderId}`);

      fetchOrders();
    });

    return () => {
      socketId.off("getOrderNotification");
    };
  }, []);

  //socket section ----------------------

  const fetchOrders = async () => {
    try {
      setIsLoading(true);
      const { response } = await orderApi.getOrdersByOperator(operator._id);
      if (response) {
        setOrders(response.data.orders || []);
      }
    } catch (err) {
      console.error("Error loading orders:", err);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch orders
  useEffect(() => {
    if (!operator?._id) return;

    fetchOrders();
  }, [operator?._id]);

  const ordersWithIndex = orders.map((order, i) => {
    const start = moment(order.timeSelected || order.createdAt).toDate();
    const end = moment(start).add(45, "minutes").toDate(); // or use order.duration

    return {
      ...order,
      start,
      end,
      index: i,
      customer: order.user?.displayName || "Guest",
      serviceList: order.selectedServices || [],
      totalPrice: order.totalPrice || 0,
    };
  });

  // 1. Group orders by day
  const ordersByDay = {};
  ordersWithIndex.forEach((order) => {
    const dayKey = moment(order.start).format("YYYY-MM-DD");
    if (!ordersByDay[dayKey]) ordersByDay[dayKey] = [];
    ordersByDay[dayKey].push(order);
  });

  // 2. Assign colors per day (cycle through palette)
  //   const colorPalette = [
  //     "#93c5fd",
  //     "#facc15",
  //     "#4ade80",
  //     "#f87171",
  //     "#f472b6",
  //     "#60a5fa",
  //   ];

  const colorPalette = [
    "linear-gradient(135deg, #f87171, #facc15)", // red → yellow
    "linear-gradient(135deg, #facc15, #f87171)", // yellow → red (reverse)
    "linear-gradient(135deg, #ef4444, #fbbf24)", // strong red → amber
    "linear-gradient(135deg, #fca5a5, #fde047)", // light red → light yellow
    "linear-gradient(135deg, #dc2626, #facc15)", // dark red → yellow
    "linear-gradient(135deg, #f87171, #fcd34d)", // red → soft gold
    "linear-gradient(135deg, #b91c1c, #f59e0b)", // deep red → amber orange
    "linear-gradient(135deg, #f43f5e, #fbbf24)", // rose red → amber
    "linear-gradient(135deg, #e11d48, #facc15)", // crimson → yellow
    "linear-gradient(135deg, #f87171, #fde68a)", // soft red → pale yellow
  ];

  const orderColors = {};

  Object.keys(ordersByDay).forEach((dayKey) => {
    ordersByDay[dayKey].forEach((order, idx) => {
      orderColors[order._id] = colorPalette[idx % colorPalette.length];
    });
  });

  if (isLoading) {
    return (
      <div className="h-screen flex items-center justify-center bg-dry">
        <ProcessingBar2 />
      </div>
    );
  }

  if (orders.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-screen text-center text-white">
        <BiUserX size={70} className="mb-6 text-redlight" />
        <h2 className="text-lg font-semibold">No Orders Found</h2>
        <p className="text-sm text-gray-400">
          You currently have no orders. When customers place new orders, they
          will appear here.
        </p>
      </div>
    );
  }

  return (
    <Layout>
      <div className="relative overflow-y-auto scrollbar-thin scrollbar-thumb-redlight scrollbar-track-gray-200">
        <Calendar
          localizer={localizer}
          //   events={parsedEvents}
          events={ordersWithIndex}
          defaultView={Views.DAY}
          views={{ day: true, week: true, month: true, agenda: true }}
          step={30}
          timeslots={2}
          defaultDate={new Date()}
          //   style={{ height: 1200, marginBottom: 50 }}
          style={{
            height: "100vh",
            backgroundColor: "#111827",
            color: "#f9fafb",
          }}
          min={new Date(0, 0, 0, 8, 0)}
          max={new Date(0, 0, 0, 22, 0)}
          components={{
            toolbar: CustomToolbarOrdering,
            event: CustomEventOrdering,
          }}
          eventPropGetter={(event) => {
            const isPast = new Date(event.end) < new Date(); // ✅ adjust if you use start instead
            const isCompleted = event.paymentStatus?.includes("completed");

            const itemCount = event.orderItems?.length || 1;

            // ✅ dynamic height per service row (e.g. 20px each + some padding)
            const minHeight = itemCount * 110 + 120; // 20px per service + header/footer

            if (isPast || isCompleted) {
              return {
                style: {
                  backgroundColor: "#d1d5db", // gray
                  borderLeft: "8px solid #9ca3af",
                  borderRadius: "0px",
                  color: "#1f2937",
                  opacity: 1,
                  textDecoration: isPast ? "line-through" : "none",
                  minHeight: `${minHeight}px`,
                  maxWidth: "200px",
                },
              };
            }

            const bg =
              orderColors[event._id] ||
              "linear-gradient(135deg, #f87171, #facc15)";
            if (event.paymentStatus?.includes("awaiting")) bg = "#facc15"; // yellow
            if (event.paymentStatus?.includes("completed")) bg = "#4ade80"; // green
            if (event.paymentStatus?.includes("cancelled")) bg = "#f87171"; // red
            return {
              style: {
                backgroundColor: "#c1c1c1",
                borderLeft: "6px solid #1f2937",
                borderRadius: "0px",
                color: "#111",
                fontWeight: "500",
                minHeight: `${minHeight}px`,
                maxHeight: "400px",
                maxWidth: "200px",
              },
            };
          }}
          slotPropGetter={(date) => {
            const isPast = moment(date).isBefore(moment());
            return {
              style: {
                backgroundColor: isPast ? "#E5E7EB" : "#fecaca", // lighter gray vs soft red
                borderColor: isPast ? "#D1D5DB" : "#fca5a5", // subtle matching border
                borderWidth: "1px",
                borderStyle: "solid",
              },
            };
          }}
        />
        <NowIndicatorLabel />
        <ReactTooltip
          id="event-tooltip"
          place="top"
          opacity={1} // important!
          className="!z-[9999] !bg-dry !text-black !shadow-md !rounded-lg !px-3 !py-1 !text-xs"
          delayShow={100}
          clickable
        />
        <OrderNotificationModal
          isOpen={modalOpen}
          onClose={() => setModalOpen(false)}
          orderId={orderData.orderId}
          orderInfo={orderData.orderInfo}
        />
      </div>
    </Layout>
  );
}

export default OrdersCalendar;
